/**
 * Lightweight publish–subscribe bus for cross-component theme events (cart, variants, sections).
 * Exposes `subscribe`, `publish`, and `PUB_SUB_EVENTS` on `window` for scripts loaded in any order (after this file).
 *
 * @fileoverview Theme pub/sub — load after `utility.js`, before `global.js` and feature scripts that publish.
 */

(function initThemePubSub() {
  /** @type {Record<string, Array<(data: unknown) => void>>} */
  const subscribers = {};

  /**
   * Canonical event names for `subscribe` / `publish`.
   * @readonly
   * @type {Readonly<{
   *   cartUpdate: string,
   *   quantityUpdate: string,
   *   variantChange: string,
   *   cartError: string,
   *   sectionRefreshed: string
   * }>}
   */
  const PUB_SUB_EVENTS = Object.freeze({
    cartUpdate: 'cart-update',
    quantityUpdate: 'quantity-update',
    variantChange: 'variant-change',
    cartError: 'cart-error',
    sectionRefreshed: 'section-refreshed',
  });

  /**
   * Registers a listener for a topic. Returns `unsubscribe` to remove it (e.g. in `disconnectedCallback`).
   *
   * @param {string} eventName - One of `PUB_SUB_EVENTS` values or a custom string.
   * @param {(data: unknown) => void} callback - Invoked synchronously when `publish` runs.
   * @returns {() => void} Unsubscribe function.
   */
  function subscribe(eventName, callback) {
    if (subscribers[eventName] === undefined) {
      subscribers[eventName] = [];
    }

    subscribers[eventName] = [...subscribers[eventName], callback];

    return function unsubscribe() {
      subscribers[eventName] = subscribers[eventName].filter((cb) => cb !== callback);
    };
  }

  /**
   * Notifies all subscribers for `eventName`, in registration order.
   *
   * @param {string} eventName - Topic name.
   * @param {unknown} [data] - Payload (e.g. `{ source, cartData }` for cart updates).
   */
  function publish(eventName, data) {
    if (!subscribers[eventName]) {
      return;
    }

    for (const callback of subscribers[eventName]) {
      callback(data);
    }
  }

  window.PUB_SUB_EVENTS = PUB_SUB_EVENTS;
  window.subscribe = subscribe;
  window.publish = publish;
})();
